import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { trackAddToCart } from "../utils/analytics";

const CartContext = createContext(undefined);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("cart_items");
      const parsed = raw ? JSON.parse(raw) : [];
      // Migrate old items to ensure they have packageQuantity as a number
      return parsed.map((item) => ({
        ...item,
        packageQuantity: Number(
          item.packageQuantity || item.package_quantity || 1
        ),
        qty: Number(item.qty || 1),
      }));
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("cart_items", JSON.stringify(items));
    } catch {}
  }, [items]);

  // UI state for cart drawer
  const [isOpen, setIsOpen] = useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((v) => !v);

  const addItem = (item) => {
    if (!item || !item.id) return;
    const packageQty = Number(
      item.package_quantity || item.packageQuantity || 1
    );
    // Use the qty from item if provided, otherwise use packageQty
    const itemQty = Number(item.qty || packageQty);
    console.log(
      "CartContext addItem - item.qty:",
      item.qty,
      "itemQty:",
      itemQty,
      "packageQty:",
      packageQty
    );
    
    // Track add to cart event
    trackAddToCart(
      {
        id: String(item.id),
        name: item.title || item.name || "",
        price: item.price ?? 0,
      },
      itemQty
    );
    
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === String(item.id));
      if (idx >= 0) {
        const next = [...prev];
        const currentQty = Number(next[idx].qty || 1);
        // Add the new quantity to the existing quantity
        next[idx] = {
          ...next[idx],
          qty: currentQty + itemQty,
          packageQuantity: packageQty, // Ensure it's always set
        };
        return next;
      }
      return [
        ...prev,
        {
          id: String(item.id),
          name: item.title || item.name || "",
          price: item.price ?? null,
          packageQuantity: packageQty,
          image:
            item.image ||
            item.image_url_1920 ||
            item.image_url ||
            (item.id
              ? `https://erp-elsalab.odoo.com/web/image/${item.id}`
              : ""),
          color: item.att_color || item.color || null,
          size: item.att_size || item.size || null,
          qty: itemQty, // Use the qty from item
        },
      ];
    });
  };

  const removeItem = (id) =>
    setItems((prev) => prev.filter((x) => x.id !== String(id)));

  const setQty = (id, qty) =>
    setItems((prev) =>
      prev.map((x) =>
        x.id === String(id) ? { ...x, qty: Math.max(1, qty) } : x
      )
    );

  // Increment quantity by package quantity (like mobile app)
  const incrementQuantity = (id) => {
    setItems((prev) =>
      prev.map((x) => {
        if (x.id === String(id)) {
          // Ensure packageQuantity exists and is a number, default to 1 if missing
          const packageQty = Number(
            x.packageQuantity || x.package_quantity || 1
          );
          const currentQty = Number(x.qty || packageQty);
          // Use multiplier approach to avoid floating point precision errors
          // Calculate how many packages we have (rounded to nearest integer), increment, then multiply back
          const numPackages = Math.round(currentQty / packageQty);
          const newNumPackages = numPackages + 1;
          const newQty = newNumPackages * packageQty;
          const updatedItem = {
            ...x,
            qty: newQty,
            packageQuantity: packageQty, // Ensure it's set for future operations
          };
          return updatedItem;
        }
        return x;
      })
    );
  };

  // Decrement quantity by package quantity (like mobile app)
  const decrementQuantity = (id) => {
    setItems((prev) =>
      prev.map((x) => {
        if (x.id === String(id)) {
          // Ensure packageQuantity exists and is a number, default to 1 if missing
          const packageQty = Number(
            x.packageQuantity || x.package_quantity || 1
          );
          const currentQty = Number(x.qty || packageQty);
          // Use multiplier approach to avoid floating point precision errors
          // Calculate how many packages we have (rounded to nearest integer), decrement, then multiply back
          const numPackages = Math.round(currentQty / packageQty);
          // Only decrement if we have more than 1 package
          if (numPackages > 1) {
            const newNumPackages = numPackages - 1;
            const newQty = newNumPackages * packageQty;
            const updatedItem = {
              ...x,
              qty: newQty,
              packageQuantity: packageQty, // Ensure it's set for future operations
            };
            return updatedItem;
          }
          // If quantity equals package quantity, don't go below it
          return { ...x, packageQuantity: packageQty }; // Ensure it's set even if not decrementing
        }
        return x;
      })
    );
  };

  const clear = () => setItems([]);

  // Count number of unique items in cart (not total quantity)
  const count = useMemo(() => items.length, [items]);
  const subtotal = useMemo(
    () => items.reduce((sum, x) => sum + (x.price || 0) * (x.qty || 1), 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      setQty,
      incrementQuantity,
      decrementQuantity,
      clear,
      count,
      subtotal,
      isOpen,
      open,
      close,
      toggle,
    }),
    [items, count, subtotal, isOpen]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
